// src/components/Navigation/DesktopSidebar.js
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Toolbar,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';

// Import icons from Material-UI
import InsightsIcon from '@mui/icons-material/Insights';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import HelpIcon from '@mui/icons-material/Help';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Import navigation config
import { NAVIGATION_CONFIG } from '../../config/navigationConfig';
import { useCoachingRole } from '../../hooks/useCoachingRole';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Desktop Sidebar Navigation
 *
 * Displays a permanent left sidebar navigation on desktop devices with:
 * - Collapsible width (280px expanded, 64px collapsed)
 * - Active route highlighting with left border
 * - Smooth transitions
 * - Persistent state in localStorage
 *
 * Does NOT render on mobile devices
 */
// eslint-disable-next-line max-lines-per-function
function DesktopSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sidebar collapsed state (persisted)
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const { isCoach } = useCoachingRole();
  const { isPlatformAdmin } = useAuth();

  // Icon mapping - matches approved icons from navigationConfig
  const iconMap = {
    'InsightsIcon': InsightsIcon,
    'SmartToyIcon': SmartToyIcon,
    'AccountTreeIcon': AccountTreeIcon,
    'MenuBookIcon': MenuBookIcon,
    'VolunteerActivismIcon': VolunteerActivismIcon,
    'SportsHandballIcon': SportsHandballIcon,
    'AdminPanelSettingsIcon': AdminPanelSettingsIcon,
    'PsychologyAltIcon': PsychologyAltIcon,
    'HelpIcon': HelpIcon,
    'AssessmentIcon': AssessmentIcon,
};

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed);
  }, [collapsed]);

  // Don't render on mobile devices
  if (isMobile) {
    return null;
  }

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const isActiveRoute = (route) => {
    return location.pathname === route;
  };

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: 200,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {/* Toolbar spacer - matches AppBar height */}
      <Toolbar />

      {/* Navigation items */}
      <List sx={{ flex: 1 }}>
        {NAVIGATION_CONFIG.primary.filter((item) => {
          if (item.requiresCoachRole && !isCoach && !isPlatformAdmin) return false;
          return true;
        }).map((item) => {
          const IconComponent = iconMap[item.icon];
          const isActive = isActiveRoute(item.route);

          return (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.route)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                  borderLeft: isActive ? `4px solid ${theme.palette.secondary.main}` : '4px solid transparent',
                  backgroundColor: isActive ? 'rgba(35, 163, 143, 0.12)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive
                      ? 'rgba(35, 163, 143, 0.16)'
                      : 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  {IconComponent && <IconComponent />}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    sx={{
                      opacity: collapsed ? 0 : 1,
                      color: '#ffffff',
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Secondary navigation */}
      {NAVIGATION_CONFIG.secondary.filter((item) => {
        if (item.adminOnly && !isPlatformAdmin) return false;
        return true;
      }).length > 0 && (
        <>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
          <List>
            {NAVIGATION_CONFIG.secondary.filter((item) => {
              if (item.adminOnly && !isPlatformAdmin) return false;
              return true;
            }).map((item) => {
              const IconComponent = iconMap[item.icon];
              const isActive = isActiveRoute(item.route);

              return (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.route)}
                    sx={{
                      minHeight: 48,
                      justifyContent: collapsed ? 'center' : 'initial',
                      px: 2.5,
                      borderLeft: isActive ? `4px solid ${theme.palette.secondary.main}` : '4px solid transparent',
                      backgroundColor: isActive ? 'rgba(35, 163, 143, 0.12)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive
                          ? 'rgba(35, 163, 143, 0.16)'
                          : 'rgba(255, 255, 255, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 3,
                        justifyContent: 'center',
                        color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      {IconComponent && <IconComponent />}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        sx={{
                          opacity: collapsed ? 0 : 1,
                          color: '#ffffff',
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {/* Divider */}
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Collapse toggle button at bottom */}
      <Box sx={{ p: 1 }}>
        <IconButton
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'}
          sx={{
            width: '100%',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Drawer>
  );
}

export default DesktopSidebar;
